import { useCallback, useState, type MouseEvent } from 'react';
import type { AnchorPosition } from '../components/ui/Popover';

export interface ContextMenuState {
  /** Viewport coordinate to anchor the menu at, or null when closed. */
  anchorPosition: AnchorPosition | null;
  /** Right-click handler to attach to the target element. */
  handleContextMenu: (event: MouseEvent<HTMLElement>) => void;
  /** Controlled-open handler to pass to <Popover onOpenChange>. */
  handleOpenChange: (open: boolean) => void;
  /** Imperatively close the menu. */
  close: () => void;
}

/**
 * Manages the open state and anchor coordinate for a right-click context menu.
 * The menu is considered open whenever `anchorPosition` is non-null, so callers
 * can mount the menu conditionally on that value.
 */
export function useContextMenu(): ContextMenuState {
  const [anchorPosition, setAnchorPosition] = useState<AnchorPosition | null>(null);

  const handleContextMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    // Let a modifier-click fall through to the browser's native context menu.
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    setAnchorPosition({ x: event.clientX, y: event.clientY });
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) setAnchorPosition(null);
  }, []);

  const close = useCallback(() => setAnchorPosition(null), []);

  return { anchorPosition, handleContextMenu, handleOpenChange, close };
}
